import { HolderOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSearch } from "@tanstack/react-router";
import { Col, Row } from "antd";
import { dashboardStateAtom, type DashboardCardId } from "atoms/dashboardState";
import { AffiliateDetailDrawer } from "components/Drawers/AffiliateDetailDrawer/AffiliateDetailDrawer";
import { useCardRegistry } from "components/Report/UI/Dashboard/shared/hooks/useDashboardCardRegistry";
import React from "react";
import { useRecoilState } from "recoil";
import { DrawerId } from "types/models";
import { zIndexes } from "utils/zIndexes";

import { CARD_HEIGHT } from "./shared/constants";
import { DashboardCard } from "./shared/DashboardCard";

type SortableAttrs = NonNullable<ReturnType<typeof useSortable>["attributes"]>;
type SortableListeners = NonNullable<ReturnType<typeof useSortable>["listeners"]>;

interface SearchParams {
    [`affiliateDetail-affiliateId`]: string;
}

const Grip: React.FC<{
    isDragging: boolean;
    attributes: SortableAttrs;
    listeners: SortableListeners;
}> = ({ isDragging, attributes, listeners }) => {
    const [hover, setHover] = React.useState(false);
    const active = isDragging || hover;

    return (
        <button
            type="button"
            aria-label="Drag card"
            {...attributes}
            {...listeners}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className={[
                "inline-grid place-items-center w-7 h-16",
                active ? "bg-[#088484] text-white" : "bg-neutral-300 text-white",
                "shadow-[0_2px_6px_rgba(0,0,0,0.12)]",
                "cursor-grab active:cursor-grabbing select-none transition-colors duration-150",
                "rounded-tl-[4px] rounded-bl-[4px]",
                "rounded-tr-none rounded-br-none",
            ].join(" ")}
        >
            <HolderOutlined style={{ fontSize: 12 }} aria-hidden />
        </button>
    );
};

const SortableItem: React.FC<{
    id: DashboardCardId;
    disabled?: boolean;
    render: (dragProps: {
        setNodeRef: (el: HTMLElement | null) => void;
        style: React.CSSProperties;
        attributes: SortableAttrs;
        listeners: SortableListeners;
        isDragging: boolean;
        disabled: boolean;
    }) => React.ReactNode;
}> = ({ id, disabled = false, render }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        willChange: transform ? "transform" : undefined,
    };

    return <>{render({ attributes: attributes!, listeners: listeners!, setNodeRef, style, isDragging, disabled })}</>;
};

const isDefined = <T,>(v: T | undefined): v is T => v !== undefined;

export const DashboardShell: React.FC = () => {
    const [state, setState] = useRecoilState(dashboardStateAtom);
    const cards = useCardRegistry();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const cardsById = React.useMemo(() => new Map(cards.map((c) => [c.id as DashboardCardId, c])), [cards]);

    const registryIds = cards.map((c) => c.id as DashboardCardId);
    const cleaned = state.order.filter((id) => cardsById.has(id)) as DashboardCardId[];
    const toAdd = registryIds.filter((id) => !cleaned.includes(id));
    const effectiveOrder: DashboardCardId[] = [...cleaned, ...toAdd];

    const allDefs = effectiveOrder.map((id) => cardsById.get(id)).filter(isDefined);
    const visibleDefs = allDefs.filter((def) => !state.hidden.includes(def.id as DashboardCardId));

    const visibleIds = visibleDefs.map((d) => d.id as DashboardCardId);

    const affiliateId = searchParams[`${DrawerId.AffiliateDetail}-affiliateId`];

    return (
        <div className="mt-10">
            {affiliateId && <AffiliateDetailDrawer affiliateId={affiliateId} />}
            <DndContext
                sensors={sensors}
                onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return;
                    const from = effectiveOrder.indexOf(active.id as DashboardCardId);
                    const to = effectiveOrder.indexOf(over.id as DashboardCardId);
                    if (from < 0 || to < 0) return;
                    setState((s) => ({
                        ...s,
                        order: arrayMove(effectiveOrder, from, to) as DashboardCardId[],
                    }));
                }}
            >
                <SortableContext items={visibleIds}>
                    <Row gutter={[30, 16]}>
                        {visibleDefs.map((def) => {
                            const draggable = def.draggable !== false;
                            const id = def.id as DashboardCardId;

                            return (
                                <Col key={id} xs={24} md={12} xl={12}>
                                    <SortableItem
                                        id={id}
                                        disabled={!draggable}
                                        render={({ setNodeRef, style, attributes, listeners, isDragging }) => (
                                            <div ref={setNodeRef} style={style} className="relative group">
                                                <div
                                                    className={[
                                                        "rounded-sm overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.06)]",
                                                        "ring-1 ring-black/5 bg-white transition-colors duration-150",
                                                        isDragging ? "outline outline-2 outline-[#088484]" : "",
                                                    ].join(" ")}
                                                    style={{ borderRadius: 10 }}
                                                >
                                                    <DashboardCard
                                                        dragging={isDragging}
                                                        title={def.title}
                                                        subtitle={def.subtitle}
                                                        fixedHeight={CARD_HEIGHT}
                                                        extra={def.extra}
                                                    >
                                                        {def.node}
                                                    </DashboardCard>
                                                </div>

                                                {draggable && (
                                                    <div
                                                        className={`
              absolute top-1/2 -translate-y-1/2 -left-4 z-${zIndexes.cardDragAndDropHandle}
              opacity-0 pointer-events-none
              group-hover:opacity-100 group-hover:pointer-events-auto
              focus-within:opacity-100 focus-within:pointer-events-auto
            `}
                                                    >
                                                        <Grip
                                                            isDragging={isDragging}
                                                            attributes={attributes}
                                                            listeners={listeners}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            );
                        })}
                    </Row>
                </SortableContext>
            </DndContext>
        </div>
    );
};
